/**
 * Plan gating — the single source of truth, shared by the API (which enforces
 * it per route) and the SPA (which renders lock pills from it).
 *
 * Ported from design/Clinic Stock v2.dc.html:4589 (PLAN_RANK) and :5292
 * (featureGate). Plan state itself is never stored per-feature: it is derived
 * from the subscription row, which follows Stripe.
 */
export const PLANS = ['starter', 'pro', 'premium'];
export const PLAN_RANK = { starter: 0, pro: 1, premium: 2 };
export const PLAN_LABEL = {
    starter: 'Starter',
    pro: 'Pro',
    premium: 'Premium',
};
/** Seat caps enforced at invite time against active + invited users. */
export const SEAT_LIMIT = {
    starter: 2,
    pro: 5,
    premium: Number.POSITIVE_INFINITY,
};
/**
 * Product cap, per the pricing table in design/PROTOTYPE-SPEC.md §2:
 * "products tracked (100 / 400 / unlimited)".
 *
 * Pro was Number.POSITIVE_INFINITY until 27 July 2026 — wrong, and the test
 * asserted the wrong value alongside it. The prototype never enforced this in
 * code (it is a pricing-page claim), so the spec is the only authority.
 */
export const PRODUCT_LIMIT = {
    starter: 100,
    pro: 400,
    premium: Number.POSITIVE_INFINITY,
};
/** Minimum plan rank each gated feature requires. Ungated features are absent. */
export const FEATURE_GATE = {
    analytics: PLAN_RANK.pro,
    promotions: PLAN_RANK.pro,
    targets: PLAN_RANK.premium,
    /**
     * The marketing assistant. Pro, not Premium: its best input is live
     * promotions, which are Pro. See docs/AI-ASSISTANT.md, decision 1.
     */
    aiAssistant: PLAN_RANK.pro,
};
export function isGatedFeature(key) {
    return key in FEATURE_GATE;
}
export function canAccess(plan, feature) {
    if (!isGatedFeature(feature))
        return true;
    return PLAN_RANK[plan] >= FEATURE_GATE[feature];
}
/** The plan name shown on the lock pill — "Pro" or "Premium". */
export function requiredPlanFor(feature) {
    const min = FEATURE_GATE[feature];
    return (PLANS.find((p) => PLAN_RANK[p] === min) ?? 'premium');
}
export function seatLimitReached(plan, activeOrInvitedSeats) {
    return activeOrInvitedSeats >= SEAT_LIMIT[plan];
}
export function productLimitReached(plan, productCount) {
    return productCount >= PRODUCT_LIMIT[plan];
}
/**
 * Subscription statuses we treat as entitled. `trialing` gets full access to the
 * plan being trialled; `past_due` keeps access while Stripe retries (dunning);
 * anything else falls back to read-only, which the API enforces by rejecting
 * writes rather than by downgrading the plan.
 */
export const ENTITLED_STATUSES = ['trialing', 'active', 'past_due'];
export function isEntitled(status) {
    return ENTITLED_STATUSES.includes(status);
}
//# sourceMappingURL=gate.js.map